/**
 * ValidationSuite.tsx — Checks
 *
 * The six gates the mapping has to clear before anything converts:
 * nothing unmapped, balances tie out, every decision closed, and so on.
 */

import React, { useState, useEffect } from 'react';
import {
  ConversionProject,
  ValidationResult,
  runValidation,
  getValidation,
} from '../../services/conversionArchitectAPI';
import {
  c, font, panel, pill, btnPrimary, btnSecondary, SEVERITY_CHIPS,
} from './tokens';
import Toast from './Toast';

interface ValidationSuiteProps {
  project: ConversionProject;
  onPhaseComplete: () => void;
}

const HEALTH_BORDER: Record<string, string> = {
  green: c.green.border,
  amber: c.amber.border,
  red: c.red.border,
  grey: c.border,
};

const HEALTH_DOT: Record<string, string> = {
  green: c.green.text2,
  amber: c.amber.solid,
  red: c.red.text,
  grey: c.textTertiary,
};

const HEALTH_LABEL: Record<string, string> = {
  green: 'Clear',
  amber: 'Open items',
  red: 'Blocking',
  grey: 'Not run',
};

const CHECK_LABELS: Record<string, { title: string; description: string }> = {
  source_coverage: {
    title: 'Every old account has a destination',
    description: 'No account from the current chart is left without a place in the new one.',
  },
  target_coverage: {
    title: 'Every new account has a source or a reason',
    description: 'Nothing in the new chart exists without something feeding it — or a documented reason.',
  },
  dollar_reconciliation: {
    title: 'Balances tie out',
    description: 'What the old accounts hold arrives intact in the new ones.',
  },
  posting_rule_consistency: {
    title: 'Posting rules stay consistent',
    description: 'Every journal pattern still points at accounts that exist in the mapping.',
  },
  attribute_completeness: {
    title: 'New details are filled in',
    description: 'Everything the new chart asks for has a value or a rule to supply one.',
  },
  entity_coverage: {
    title: 'Every entity is covered',
    description: 'The mapping holds for every legal entity in scope, not just most of them.',
  },
};

function severityChip(severity: string): React.ReactNode {
  const s = SEVERITY_CHIPS[severity] || SEVERITY_CHIPS.medium;
  return <span style={pill(s.bg, s.fg, s.border)}>{severity}</span>;
}

const statLabel: React.CSSProperties = { fontSize: 10.5, fontWeight: 700, color: c.textSecondary };
const statValue: React.CSSProperties = { fontFamily: font.mono, fontSize: 14, fontWeight: 800, color: c.textPrimary };

const ValidationSuite: React.FC<ValidationSuiteProps> = ({
  project,
  onPhaseComplete,
}) => {
  const [validation, setValidation] = useState<ValidationResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [expandedCheck, setExpandedCheck] = useState<string | null>(null);
  const [toast, setToast] = useState('');

  useEffect(() => {
    loadValidation();
  }, [project.id]);

  const loadValidation = async () => {
    setLoading(true);
    try {
      const data = await getValidation(project.id);
      setValidation(data);
    } catch {
      // No validation yet
    } finally {
      setLoading(false);
    }
  };

  const handleRunValidation = async () => {
    setRunning(true);
    try {
      const data = await runValidation(project.id);
      setValidation(data);
      setToast('Checks complete');
    } catch (err: any) {
      console.error('Validation failed:', err);
      setToast(err.response?.data?.error || 'The checks could not run');
    } finally {
      setRunning(false);
    }
  };

  if (loading) {
    return (
      <div style={{ ...panel, fontFamily: font.ui, fontSize: 13, color: c.textSecondary }}>
        Loading the checks…
      </div>
    );
  }

  if (!validation) {
    return (
      <div style={{ fontFamily: font.ui }}>
        <div style={{ ...panel, textAlign: 'center', padding: '48px 24px' }}>
          <h2 style={{ margin: '0 0 10px', fontSize: 20, fontWeight: 800, color: c.textPrimary }}>
            Run the checks
          </h2>
          <p style={{ margin: '0 auto 20px', maxWidth: 520, fontSize: 13, color: c.textBody, lineHeight: 1.6 }}>
            Six gates the mapping has to clear before go-live: coverage both ways, balances,
            posting rules, new details, and entity consistency.
          </p>
          <button
            type="button"
            style={{ ...btnPrimary(running), fontSize: 14, padding: '11px 22px' }}
            onClick={handleRunValidation}
            disabled={running}
          >
            {running ? 'Running the checks…' : 'Run the checks'}
          </button>
        </div>
        <Toast message={toast} onClose={() => setToast('')} />
      </div>
    );
  }

  const overallHealth = validation.overall_health;
  const overallStatus = overallHealth.status;
  const banner = overallStatus === 'green'
    ? { bg: c.green.fillSoft, border: c.green.border, fg: c.green.text, label: 'Ready for go-live checks' }
    : overallStatus === 'red'
      ? { bg: c.red.fill, border: c.red.border, fg: c.red.text, label: 'Blocking issues' }
      : { bg: c.amber.fillSoft, border: c.amber.border, fg: c.amber.text, label: 'Still open items' };

  const checks = [
    { key: 'source_coverage', data: validation.source_coverage },
    { key: 'target_coverage', data: validation.target_coverage },
    { key: 'dollar_reconciliation', data: validation.dollar_reconciliation },
    { key: 'posting_rule_consistency', data: validation.posting_rule_consistency },
    { key: 'attribute_completeness', data: validation.attribute_completeness },
    { key: 'entity_coverage', data: validation.entity_coverage },
  ];

  return (
    <div style={{ fontFamily: font.ui }}>
      {/* Overall readiness banner */}
      <div
        style={{
          background: banner.bg,
          border: `1px solid ${banner.border}`,
          borderRadius: 12,
          padding: '14px 20px',
          marginBottom: 16,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ fontSize: 15, fontWeight: 800, color: banner.fg }}>{banner.label}</div>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center', fontSize: 12, color: c.textBody }}>
          <span><strong style={{ color: c.green.text2, fontFamily: font.mono }}>{overallHealth.checks_passed}</strong> clear</span>
          <span><strong style={{ color: c.amber.text2, fontFamily: font.mono }}>{overallHealth.checks_warning}</strong> open</span>
          <span><strong style={{ color: c.red.text, fontFamily: font.mono }}>{overallHealth.checks_failed}</strong> blocking</span>
          <span style={{ fontSize: 11, color: c.textSecondary }}>
            Checked {new Date(validation.computed_at).toLocaleString()}
          </span>
        </div>
      </div>

      {/* Checks grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 14 }}>
        {checks.map(({ key, data }) => {
          const health = (data as any).health || 'grey';
          const label = CHECK_LABELS[key];
          const isExpanded = expandedCheck === key;
          const checkFindings = validation.findings.filter(f => f.check === key);

          return (
            <div
              key={key}
              style={{
                background: c.card,
                border: `1px solid ${HEALTH_BORDER[health] || c.border}`,
                borderRadius: 12,
                padding: '14px 16px',
              }}
            >
              <button
                type="button"
                onClick={() => setExpandedCheck(isExpanded ? null : key)}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 10,
                  width: '100%',
                  background: 'transparent',
                  border: 'none',
                  padding: 0,
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontFamily: font.ui,
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    width: 10, height: 10, borderRadius: '50%', flexShrink: 0, marginTop: 4,
                    background: HEALTH_DOT[health] || c.textTertiary,
                  }}
                />
                <span style={{ flex: 1 }}>
                  <span style={{ display: 'block', fontSize: 13.5, fontWeight: 800, color: c.textPrimary }}>
                    {label.title}
                  </span>
                  <span style={{ display: 'block', fontSize: 12, color: c.textSecondary, marginTop: 3, lineHeight: 1.5 }}>
                    {label.description}
                  </span>
                </span>
                <span style={{ fontSize: 11, fontWeight: 800, color: HEALTH_DOT[health] || c.textTertiary, whiteSpace: 'nowrap' }}>
                  {HEALTH_LABEL[health] || health}
                </span>
              </button>

              {/* Per-check findings */}
              {checkFindings.length > 0 && (
                <div style={{ marginTop: 10, borderTop: `1px solid ${c.hairline}`, paddingTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {checkFindings.map((finding, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                      {severityChip(finding.severity)}
                      <span style={{ fontSize: 12, color: c.textBody, lineHeight: 1.5 }}>{finding.message}</span>
                    </div>
                  ))}
                </div>
              )}

              {isExpanded && (
                <div style={{ marginTop: 12, borderTop: `1px solid ${c.hairline}`, paddingTop: 12 }}>
                  {key === 'source_coverage' && (
                    <div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
                        <div><div style={statLabel}>Accounts</div><div style={statValue}>{validation.source_coverage.total}</div></div>
                        <div><div style={statLabel}>With a home</div><div style={statValue}>{validation.source_coverage.mapped}</div></div>
                        <div><div style={statLabel}>Without one</div><div style={{ ...statValue, color: validation.source_coverage.unmapped > 0 ? c.amber.text2 : c.textPrimary }}>{validation.source_coverage.unmapped}</div></div>
                        <div><div style={statLabel}>Covered</div><div style={statValue}>{(validation.source_coverage.coverage_rate * 100).toFixed(1)}%</div></div>
                      </div>
                      {validation.source_coverage.unmapped_accounts.length > 0 && (
                        <div style={{ marginTop: 10 }}>
                          <div style={{ ...statLabel, marginBottom: 6 }}>Accounts without a home:</div>
                          {validation.source_coverage.unmapped_accounts.map((a, i) => (
                            <div key={i} style={{ fontSize: 12, color: c.textBody, padding: '2px 0' }}>
                              <span style={{ fontFamily: font.mono, color: c.textSecondary }}>{a.code}</span> — {a.name}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {key === 'target_coverage' && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
                      <div><div style={statLabel}>New accounts</div><div style={statValue}>{validation.target_coverage.total}</div></div>
                      <div><div style={statLabel}>Fed by the old chart</div><div style={statValue}>{validation.target_coverage.mapped_from_source}</div></div>
                      <div><div style={statLabel}>Nothing feeding them</div><div style={{ ...statValue, color: validation.target_coverage.no_source_mapping > 0 ? c.amber.text2 : c.textPrimary }}>{validation.target_coverage.no_source_mapping}</div></div>
                    </div>
                  )}

                  {key === 'dollar_reconciliation' && (
                    validation.dollar_reconciliation.status === 'not_available' ? (
                      <p style={{ margin: 0, fontSize: 12, color: c.textSecondary }}>{validation.dollar_reconciliation.reason}</p>
                    ) : (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
                        <div><div style={statLabel}>Old chart total</div><div style={statValue}>${validation.dollar_reconciliation.source_total?.toLocaleString()}</div></div>
                        <div><div style={statLabel}>New chart total</div><div style={statValue}>${validation.dollar_reconciliation.target_total?.toLocaleString()}</div></div>
                        <div>
                          <div style={statLabel}>Difference</div>
                          <div style={{ ...statValue, color: (validation.dollar_reconciliation.variance || 0) !== 0 ? c.amber.text2 : c.textPrimary }}>
                            ${validation.dollar_reconciliation.variance?.toLocaleString()}
                            {' '}({((validation.dollar_reconciliation.variance_pct || 0) * 100).toFixed(2)}%)
                          </div>
                        </div>
                      </div>
                    )
                  )}

                  {key === 'posting_rule_consistency' && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
                      <div><div style={statLabel}>Rules checked</div><div style={statValue}>{validation.posting_rule_consistency.total_posting_rules_checked}</div></div>
                      <div><div style={statLabel}>Broken references</div><div style={{ ...statValue, color: validation.posting_rule_consistency.orphan_legs > 0 ? c.amber.text2 : c.textPrimary }}>{validation.posting_rule_consistency.orphan_legs}</div></div>
                    </div>
                  )}

                  {key === 'attribute_completeness' && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
                      <div><div style={statLabel}>Details required</div><div style={statValue}>{validation.attribute_completeness.total_required_attributes}</div></div>
                      <div><div style={statLabel}>Filled in</div><div style={statValue}>{validation.attribute_completeness.populated}</div></div>
                      <div><div style={statLabel}>Missing</div><div style={{ ...statValue, color: validation.attribute_completeness.gaps > 0 ? c.amber.text2 : c.textPrimary }}>{validation.attribute_completeness.gaps}</div></div>
                      <div><div style={statLabel}>Complete</div><div style={statValue}>{(validation.attribute_completeness.completeness_rate * 100).toFixed(1)}%</div></div>
                    </div>
                  )}

                  {key === 'entity_coverage' && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
                      <div><div style={statLabel}>Entities in scope</div><div style={statValue}>{validation.entity_coverage.entities_in_scope}</div></div>
                      <div><div style={statLabel}>With open issues</div><div style={{ ...statValue, color: validation.entity_coverage.entities_with_issues > 0 ? c.amber.text2 : c.textPrimary }}>{validation.entity_coverage.entities_with_issues}</div></div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 18 }}>
        <button type="button" style={btnSecondary(running)} onClick={handleRunValidation} disabled={running}>
          {running ? 'Re-running…' : 'Re-run the checks'}
        </button>
        <button
          type="button"
          style={{ ...btnPrimary(), fontSize: 14, padding: '11px 22px' }}
          onClick={onPhaseComplete}
        >
          Generate the documents →
        </button>
      </div>

      <Toast message={toast} onClose={() => setToast('')} />
    </div>
  );
};

export default ValidationSuite;
